import { Stack } from '@mui/material';
import { z } from 'zod';

import { api, isDefined } from 'common';

import { type AsynchronousJobVariant, VARIABLE_REGEX } from './common';
import { BlockValueTemplateField } from '../../../ConfigFields/CodeMirror/FlowTemplate';

import JobKind = api.RpaJob.Kind;

export const ExportPrevisitPlanningVariant: AsynchronousJobVariant = {
  schema: z.object({
    jobKind: z.literal(JobKind.ExportPrevisitPlanning),
    payload: z
      .object({
        content: z.string().optional(),
        id: z.string().optional(),
        created: z.string().optional(),
        fileFormat: z.string().optional(),
        action: z
          .string()
          .refine(
            (action) =>
              isDefined(action) &&
              (['CREATE', 'DELETE'].includes(action) || VARIABLE_REGEX.test(action.trim()))
          ),
        patientEhrId: z.string().min(1),
        patientFirstName: z.string().min(1),
        patientLastName: z.string().min(1),
        patientDateOfBirth: z.string().min(1),
        patientSex: z
          .string()
          .refine(
            (patientSex) =>
              isDefined(patientSex) &&
              (Object.values(api.Patient.Sex).includes(patientSex as api.Patient.Sex) ||
                VARIABLE_REGEX.test(patientSex.trim()))
          ),
        encounterEhrReferenceValue: z.string().min(1),
        encounterId: z.string().optional(),
        ehrInformationId: z.string().optional(),
        providerUserId: z.string().optional(),
      })
      .superRefine((payload, ctx) => {
        const { content, action, created, fileFormat, id } = payload;
        const contentValid =
          action === 'CREATE' ? isDefined(content) && content.length > 0 : !content;
        if (!contentValid) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'The content is required if action is CREATE and must be empty if DELETE',
            path: ['content'],
          });
        }

        const idValid = action === 'DELETE' ? isDefined(id) && id.length > 0 : true;
        if (!idValid) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'id is required when action is DELETE',
            path: ['id'],
          });
        }

        const createdValid = action === 'DELETE' ? !created : true;
        if (!createdValid) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'The created must be empty when action is DELETE',
            path: ['created'],
          });
        }

        const fileFormatValid =
          action === 'DELETE'
            ? !fileFormat
            : isDefined(fileFormat) &&
              (['RTF', 'TXT'].includes(fileFormat) || VARIABLE_REGEX.test(fileFormat.trim()));
        if (!fileFormatValid) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `The note file format must be one of 'RTF' or 'TXT' or a reference to a variable. If action is DELETE, file format must be empty`,
            path: ['fileFormat'],
          });
        }
      }),
  }),
  Config: () => {
    return (
      <Stack spacing={2}>
        <BlockValueTemplateField
          fieldName="config.payload.action"
          label="Action"
          helperText="Enter the desired behavior of the note"
          placeholder="e.g. CREATE"
        />
        <BlockValueTemplateField
          fieldName="config.payload.content"
          label="Note"
          helperText="Enter the content of the note to export. e.g. `{{llm.data}}`"
          placeholder="e.g. {{llm.data}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.fileFormat"
          label="Note Format"
          helperText="Enter the format of the note. e.g. RTF or TXT"
        />
        <BlockValueTemplateField
          fieldName="config.payload.id"
          label="Note ID"
          helperText="Enter a globally unique identifier for the note. Default: Random UUID."
        />
        <BlockValueTemplateField
          fieldName="config.payload.created"
          label="Note Creation Time"
          helperText="Enter the time of the note's creation. Default: Current time at execution"
        />
        <BlockValueTemplateField
          fieldName="config.payload.patientEhrId"
          label="MRN"
          helperText="Enter the reference to the patient's MRN. e.g. `{{patient.data.patientEhrId}}`"
          placeholder="e.g. {{patient.data.patientEhrId}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.patientFirstName"
          label="Patient First Name"
          helperText="Enter the reference to the patient's first name. e.g. `{{patient.data.firstName}}`"
          placeholder="e.g. {{patient.data.firstName}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.patientLastName"
          label="Patient Last Name"
          helperText="Enter the reference to the patient's last name. e.g. `{{patient.data.lastName}}`"
          placeholder="e.g. {{patient.data.lastName}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.patientDateOfBirth"
          label="Patient Date of Birth"
          helperText="Enter the reference to the patient's date of birth. e.g. `{{patient.data.dateOfBirth}}`"
          placeholder="e.g. {{patient.data.dateOfBirth}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.patientSex"
          label="Patient Sex"
          helperText="Enter the reference to the patient's sex. e.g. `{{patient.data.sex}}`"
          placeholder="e.g. {{patient.data.sex}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.encounterEhrReferenceValue"
          label="FIN"
          helperText="Enter the reference to the encounter's FIN. e.g. `{{encounter.data.ehrReferenceValue}}`"
          placeholder="e.g. {{encounter.data.ehrReferenceValue}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.encounterId"
          label="Notable Encounter ID"
          helperText="Enter the reference to the Notable Encounter ID. e.g. `{{encounter.data.id}}`"
          placeholder="e.g. {{encounter.data.id}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.ehrInformationId"
          label="Notable Ehr Information ID"
          helperText="Enter the reference to the Notable Ehr Information ID. e.g. `{{encounter.data.ehrInformationId}}`"
          placeholder="e.g. {{encounter.data.ehrInformationId}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.providerUserId"
          label="Notable Provider User ID"
          helperText="Enter the reference to the Notable Provider User ID e.g. `{{encounter.data.providerUserId}}`"
          placeholder="e.g. {{encounter.data.providerUserId}}"
        />
      </Stack>
    );
  },
};
